import { Graphics } from 'pixi.js';
import { TILE_SIZE } from '../main.js';
import { DebugLog } from '../core/DebugLog.js';

const MAX_RELICS = 4;

/**
 * Manages equipped relics and their passive/triggered effects.
 */
export class RelicSystem {
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.relics = []; // array of { relicId, relicData }
  }

  /**
   * Equip a relic. Max 4 relics.
   * @param {string} relicId - Relic key from relics.json
   * @param {object} relicData - Relic data object
   * @returns {boolean} True if equipped successfully
   */
  equip(relicId, relicData) {
    if (this.relics.length >= MAX_RELICS) return false;

    // Don't allow duplicates
    if (this.relics.some(r => r.relicId === relicId)) return false;

    this.relics.push({ relicId, relicData });
    DebugLog.info('RelicSystem', `Equipped relic: ${relicData.name}`);
    return true;
  }

  /**
   * Remove and return a relic at the given index.
   * @param {number} index - Index in relics array
   * @returns {object|null} The removed { relicId, relicData } or null
   */
  remove(index) {
    if (index < 0 || index >= this.relics.length) return null;
    const removed = this.relics.splice(index, 1)[0];
    DebugLog.info('RelicSystem', `Removed relic: ${removed.relicData.name}`);
    return removed;
  }

  /**
   * Swap relic at index with a new relic.
   * @param {number} index - Index to replace
   * @param {string} relicId - New relic id
   * @param {object} relicData - New relic data
   * @returns {object} The replaced { relicId, relicData }
   */
  swap(index, relicId, relicData) {
    const old = this.relics[index];
    this.relics[index] = { relicId, relicData };
    DebugLog.info('RelicSystem', `Swapped relic: ${old.relicData.name} -> ${relicData.name}`);
    return old;
  }

  /**
   * Aggregate all relic modifiers into a single object.
   * @returns {object} Combined modifiers
   */
  getModifiers() {
    const mods = {
      damageMod: 0,
      hpMod: 0,
      moveMod: 0,
      speedMod: 0,
      defenseMod: 0,
      goldMod: 0,
      lifesteal: 0,
      critChance: 0,
      critMult: 0,
      doubleLoot: 0,
    };

    for (const { relicData } of this.relics) {
      if (relicData.damageMod) mods.damageMod += relicData.damageMod;
      if (relicData.hpMod) mods.hpMod += relicData.hpMod;
      if (relicData.moveMod) mods.moveMod += relicData.moveMod;
      if (relicData.speedMod) mods.speedMod += relicData.speedMod;
      if (relicData.defenseMod) mods.defenseMod += relicData.defenseMod;
      if (relicData.goldMod) mods.goldMod += relicData.goldMod;
      if (relicData.lifesteal) mods.lifesteal += relicData.lifesteal;
      if (relicData.critChance) mods.critChance += relicData.critChance;
      if (relicData.critMult) mods.critMult = Math.max(mods.critMult, relicData.critMult);
      if (relicData.doubleLoot) mods.doubleLoot += relicData.doubleLoot;
    }

    return mods;
  }

  /**
   * Trigger on-kill effects (e.g. Bloodstone healing).
   * @param {object} player - Player entity
   */
  onKill(player) {
    for (const { relicData } of this.relics) {
      if (relicData.onKill) {
        if (relicData.onKill.heal) {
          player.hp = Math.min(player.maxHp, player.hp + relicData.onKill.heal);
        }
      }
    }
  }

  /**
   * Trigger on-dodge effects (e.g. Shadow Cloak afterimage).
   * @param {object} player - Player entity
   * @param {Container} worldContainer - World container for adding visuals
   * @param {Enemy[]} enemies - Array of enemies to check for afterimage damage
   * @returns {object[]} Array of effect descriptors for GameplayScene
   */
  onDodge(player, worldContainer, enemies) {
    const effects = [];

    for (const { relicData } of this.relics) {
      if (relicData.onDodge && relicData.onDodge.afterimageDamage) {
        const damage = relicData.onDodge.afterimageDamage;

        // Create damaging afterimage at player's position
        effects.push({
          type: 'afterimage',
          x: player.x,
          y: player.y,
          color: parseInt(relicData.color, 16),
          duration: 0.5,
          damage: damage,
        });

        // Damage nearby enemies
        const px = player.x + TILE_SIZE / 2;
        const py = player.y + TILE_SIZE / 2;
        const range = TILE_SIZE * 2;

        if (enemies) {
          for (const enemy of enemies) {
            if (!enemy.isAlive || enemy.state === 'dead') continue;
            const ex = enemy.x + TILE_SIZE / 2;
            const ey = enemy.y + TILE_SIZE / 2;
            const dx = ex - px;
            const dy = ey - py;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist <= range) {
              const kbLen = dist || 1;
              enemy.takeDamage(damage, dx / kbLen, dy / kbLen);
              effects.push({
                type: 'damage',
                x: ex,
                y: ey,
                amount: damage,
              });
            }
          }
        }
      }
    }

    return effects;
  }

  /**
   * Trigger on-damage-dealt effects (e.g. Vampiric Fang lifesteal).
   * @param {object} player - Player entity
   * @param {number} damage - Damage dealt
   */
  onDamageDealt(player, damage) {
    const mods = this.getModifiers();
    if (mods.lifesteal > 0) {
      const healAmount = Math.floor(damage * mods.lifesteal);
      if (healAmount > 0) {
        player.hp = Math.min(player.maxHp, player.hp + healAmount);
      }
    }
  }

  /**
   * Check if any relic provides a crit. Returns { isCrit, multiplier }.
   * @returns {object} { isCrit: boolean, multiplier: number }
   */
  rollCrit() {
    const mods = this.getModifiers();
    if (mods.critChance > 0 && Math.random() < mods.critChance) {
      return { isCrit: true, multiplier: mods.critMult || 2.0 };
    }
    return { isCrit: false, multiplier: 1.0 };
  }

  /**
   * Check if loot should be doubled (Lucky Coin).
   * @returns {boolean}
   */
  shouldDoubleLoot() {
    const mods = this.getModifiers();
    if (mods.doubleLoot > 0 && Math.random() < mods.doubleLoot) {
      return true;
    }
    return false;
  }

  /**
   * Get the current relics array.
   * @returns {object[]} Array of { relicId, relicData }
   */
  getRelics() {
    return this.relics;
  }

  /**
   * Check if at max capacity.
   * @returns {boolean}
   */
  isFull() {
    return this.relics.length >= MAX_RELICS;
  }
}
